"use client";

import React from "react";
import { ChevronLeft, ChevronRight, ShieldCheck, FileCheck2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice, type EcommerceProduct } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  product_cat: "17",
  per_page: 6,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function UsedBullsConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    currentPage,
    totalPages,
    nextPage,
    prevPage
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({
        length: 6
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col border border-border bg-card p-0 rounded-none animate-pulse">
            <div className="aspect-[16/10] w-full bg-muted" />
            <div className="p-6 space-y-4">
              <div className="h-4 w-24 bg-muted" />
              <div className="h-6 w-3/4 bg-muted" />
              <div className="space-y-2 py-3 border-y border-border">
                <div className="h-4 w-full bg-muted" />
                <div className="h-4 w-2/3 bg-muted" />
              </div>
              <div className="h-8 w-1/3 bg-muted" />
              <div className="h-10 w-full bg-muted" />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-muted/40 p-8 text-center rounded-none">
        <p className="text-base text-foreground font-medium">
          Unable to load used equipment listings.
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Please check your network connection or reload the inventory sheet.
        </p>
        <div className="mt-4">
          <Button variant="outline" onClick={() => refetch()} className="rounded-none border-foreground text-foreground uppercase tracking-wider font-serif">
            Retry inventory fetch
          </Button>
        </div>
      </div>;
  }
  if (products.length === 0) {
    return <div className="border border-border bg-card p-12 text-center rounded-none">
        <ShieldCheck className="mx-auto size-12 text-muted-foreground" />
        <h3 className="mt-4 text-xl font-bold font-serif uppercase tracking-wide text-foreground">
          No used bull inventory currently listed
        </h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          All pre-owned commercial systems have passed through recent acquisitions. Contact dispatch directly for upcoming intake machinery.
        </p>
        <div className="mt-6">
          <Button asChild variant="outline" className="rounded-none border-foreground">
            <Link to="/ContactPage">Contact dispatch office</Link>
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product: EcommerceProduct, i: number) => {
        const sku = product.customFields?.sku || `UB-${product.id.toString().padStart(4, "0")}`;
        const modelMap: Record<number, string> = {
          323: "Commercial Used",
          317: "Used Rodeo",
          15: "Vintage Rodeo 1800",
          13: "RanchMaster X1"
        };
        const model = modelMap[product.id] || product.customFields?.model || product.title.replace(/mechanical bull/i, "").trim() || product.title;
        const availability = product.is_in_stock !== undefined ? product.is_in_stock ? "In Stock" : "Available to Order" : "Available";
        const year = product.customFields?.year || product.customFields?.manufacture_year;
        const usage = product.customFields?.usage || product.customFields?.operational_hours || product.customFields?.hours;
        const includedEquipment = product.customFields?.included_equipment || product.customFields?.equipment_included || product.customFields?.included;
        const knownIssues = product.customFields?.known_issues || product.customFields?.issues;
        const voltage = product.customFields?.voltage || product.customFields?.power;
        const mattress = product.customFields?.mattress_style || product.customFields?.pad;
        const hasUsablePrice = (() => {
          const isZeroOrEmpty = (val: unknown) => {
            if (val === undefined || val === null || val === "" || val === 0 || val === "0" || val === "0.00") {
              return true;
            }
            if (typeof val === "string") {
              const cleaned = val.replace(/[^0-9.]/g, "");
              if (cleaned === "" || Number(cleaned) === 0) return true;
            }
            return false;
          };
          return !isZeroOrEmpty(product.price) || !isZeroOrEmpty(product.formatted_price) || !isZeroOrEmpty(product.formatted_discounted_price);
        })();
        return <Card key={product.id} data-index={i} className="flex flex-col justify-between border border-border bg-card rounded-none transition-[border-color] duration-200 hover:border-foreground py-0 overflow-hidden">
              <div>
                <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted border-b border-border p-4">
                  {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-contain" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                      <FileCheck2 className="size-10 stroke-[1.5]" />
                    </div>}
                </div>

                <CardHeader className="p-6 pb-2 space-y-1">
                  <div className="text-xs text-muted-foreground font-mono">
                    <span>SKU: {sku}</span>
                  </div>
                  <Link to={product.link || "#"} className="block group-hover:text-primary transition-colors">
                    <h3 className="text-xl font-bold font-serif uppercase tracking-wide text-foreground hover:text-primary transition-colors">
                      {product.title}
                    </h3>
                  </Link>
                </CardHeader>

                <CardContent className="px-6 py-3">
                  <div className="divide-y divide-border border-y border-border text-xs">
                    <div className="flex items-center justify-between py-2 font-mono">
                      <span className="text-muted-foreground">Model</span>
                      <span className="font-medium text-foreground text-right">{model}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 font-mono">
                      <span className="text-muted-foreground">Condition</span>
                      <span className="font-medium text-foreground">Used</span>
                    </div>
                    <div className="flex items-center justify-between py-2 font-mono">
                      <span className="text-muted-foreground">Availability</span>
                      <span className="font-medium text-foreground">{availability}</span>
                    </div>
                    {year && <div className="flex items-center justify-between py-2 font-mono">
                        <span className="text-muted-foreground">Year</span>
                        <span className="font-medium text-foreground">{year}</span>
                      </div>}
                    {usage && <div className="flex items-center justify-between py-2 font-mono">
                        <span className="text-muted-foreground">Usage</span>
                        <span className="font-medium text-foreground">{usage}</span>
                      </div>}
                    {voltage && <div className="flex items-center justify-between py-2 font-mono">
                        <span className="text-muted-foreground">Voltage</span>
                        <span className="font-medium text-foreground">{voltage}</span>
                      </div>}
                    {mattress && <div className="flex items-center justify-between py-2 font-mono">
                        <span className="text-muted-foreground">Mattress</span>
                        <span className="font-medium text-foreground">{mattress}</span>
                      </div>}
                    {includedEquipment && <div className="flex items-center justify-between py-2 font-mono">
                        <span className="text-muted-foreground">Included Equipment</span>
                        <span className="font-medium text-foreground text-right">{includedEquipment}</span>
                      </div>}
                    {knownIssues && <div className="flex items-center justify-between py-2 font-mono">
                        <span className="text-muted-foreground">Known Issues</span>
                        <span className="font-medium text-foreground text-right">{knownIssues}</span>
                      </div>}
                  </div>

                  <div className="mt-4 flex items-baseline justify-between">
                    <div>
                      <span className="block text-xs font-mono text-muted-foreground uppercase">
                        Unit Price
                      </span>
                      {hasUsablePrice ? <ProductPrice product={product} className="text-2xl font-bold font-serif text-foreground tracking-tight" /> : <span className="text-2xl font-bold font-serif text-foreground tracking-tight">
                          Price on Request
                        </span>}
                    </div>
                  </div>
                </CardContent>
              </div>

              <CardFooter className="flex flex-col gap-2 p-6 pt-2">
                <Button asChild className="bg-primary text-primary-foreground font-serif font-semibold uppercase tracking-wider h-11 w-full rounded-none hover:bg-primary/90 text-sm transition-colors">
                  <Link to="/RequestQuotePage">
                    Enquire About This Bull
                  </Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="w-full rounded-none border-border font-serif uppercase text-xs tracking-wider text-muted-foreground hover:text-foreground hover:border-foreground">
                  <Link to={product.link || "#"}>
                    View Details
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-between border-t border-border pt-6">
          <p className="text-xs font-mono text-muted-foreground">
            Page {currentPage} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrev} className="rounded-none border-border text-foreground font-mono text-xs disabled:opacity-40">
              <ChevronLeft className="size-4 mr-1" />
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNext} className="rounded-none border-border text-foreground font-mono text-xs disabled:opacity-40">
              Next
              <ChevronRight className="size-4 ml-1" />
            </Button>
          </div>
        </div>}
    </div>;
}
export default function Catalog() {
  return <section id="product-catalog" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="mb-10 max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2 w-2 bg-primary" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              USED BULL INVENTORY
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif uppercase tracking-tight text-foreground">
            Shop Our Used Mechanical Bull Inventory
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
            The best way to compare used equipment is to look at the individual machines currently available. Browse our used mechanical bulls for sale below to compare prices, condition, availability and the information supplied for each unit. Open the individual product page for more detail before sending an enquiry.
          </p>
        </div>

        <EcommerceProductsProvider className="w-full" wp_query={{
        product_cat: [17],
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <UsedBullsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}